<template>
  <form @submit.prevent="submit">
    <LoadingState v-if="status.isLoading || !domainsReady" />
    <ErrorState v-else-if="status.isErrored" />
    <template v-else-if="status.isLoaded && domainsReady">
      <div class="mb-4">{{ $t("global.letsencrypt-issue-desc") }}</div>
      <template v-if="domainsAvailable()">
        <div v-for="(d, index) in getIssuableDomains()" :key="index" class="d-flex">
          <Toggle
            v-model="fields.domains[d.domain]"
            :value="d.onCertificate"
            :disabled="!d.live"
            @change="detectDirty"
          />
          <div :class="d.live ? '' : 'text-secondary'">{{ d.domain }}</div>
        </div>
      </template>
      <EmptyState
        v-else
        image="ssl"
        :imageAlt="$t('global.letsencrypt')"
        :message="$t('global.no-additional-letsencrypt-domains')"
        :subMessage="$t('global.click-refresh-to-update-list')"
      />
    </template>
    <div class="form-buttons text-right mt-4">
      <Btn
        class="mr-2"
        variant="light"
        type="button"
        :disabled="loading"
        @click="refresh"
        data-test="refresh-letsencrypt-domains-button"
      >
        {{ $t("global.refresh") }}
      </Btn>
      <Btn
        variant="primary"
        type="submit"
        :disabled="loading || !dirty"
        :loading="loading"
        data-test="issue-letsencrypt-certificate-button"
      >
        {{ $t("global.issue-certificate") }}
      </Btn>
    </div>
  </form>
</template>

<script>
import { computed, onMounted, reactive, ref } from "@vue/composition-api";
import Btn from "@/components/display/Btn";
import EmptyState from "@/components/display/empty-state/EmptyState";
import ErrorState from "@/components/display/LoadingState";
import LetsEncryptIssueCertModal from "@/components/smart/modals/LetsEncryptIssueCert";
import LoadingState from "@/components/display/LoadingState";
import store from "@/store";
import Toggle from "@/components/display/Toggle";

export default {
  name: "LetsEncryptIssueCertForm",
  props: {
    site: Object,
  },
  components: {
    Btn,
    Toggle,
    ErrorState,
    LoadingState,
    EmptyState,
  },
  setup(props) {
    const loading = ref(false);
    const domains = computed(() => store.getters["letsencrypt/getDomains"]);
    const status = computed(() => store.getters["letsencrypt/getDomainsStatus"]);
    const fields = reactive({ domains: {} });
    const dirty = ref(false);

    // get list of domains to show in the form
    const getIssuableDomains = () => {
      // don't include the site's domain in the list
      return domains.value.filter(({ domain }) => !domain.match(props.site.originalDomain));
    };

    // if any fields are dirty, will set 'dirty' to true
    const detectDirty = () => {
      // assume all clean
      dirty.value = false;
      // check for any values in the list that have been changed
      domains.value.forEach(({ domain, onCertificate }) => {
        if (fields.domains[domain] !== onCertificate) {
          dirty.value = true;
        }
      });
    };

    // get a fresh list of domains (clear cache, fetch all new)
    const refresh = () => {
      loading.value = true;
      store.dispatch("letsencrypt/refreshDomains").finally(() => {
        loading.value = false;
      });
    };

    const submit = () => {
      const domainList = [];
      // get all domains from list that have been selected (toggled on)
      getIssuableDomains().forEach(({ domain }) => {
        if (fields.domains[domain]) {
          domainList.push(domain);
        }
      });

      // open confirmation modal with existing certificate warning
      store.dispatch("modals/open", {
        template: LetsEncryptIssueCertModal,
        data: { site: props.site, domains: domainList },
      });
    };

    const domainsReady = ref(false);
    const domainsAvailable = () => !!Object.keys(fields.domains).length;

    onMounted(() => {
      // get all domains available to be covered by letsencrypt
      store.dispatch("letsencrypt/getDomains", props.site.id).then(() => {
        // populate 'fields' with domains
        domains.value.forEach(({ domain, onCertificate }) => {
          fields.domains[domain] = onCertificate;
        });
        domainsReady.value = true;
      });
    });

    return {
      fields,
      domains,
      status,
      loading,
      refresh,
      submit,
      dirty,
      detectDirty,
      getIssuableDomains,
      domainsReady,
      domainsAvailable,
    };
  },
};
</script>
<style lang="scss" scoped></style>
